<script setup lang="ts">
import { isSilentFocus } from '@/Composables/useAutofocusFirstField'
import VEmptyState from '@/Components/BaseComponents/VEmptyState.vue'
/**
 * Doorzoekbare keuzelijst (headless UI Combobox). Opvolger van
 * Components/Form/Dropdown-input.
 *
 * Bewust GEEN VFormField rond dit control: `.v-input input` zet een eigen rand en die
 * wint in specificiteit van de `border-none` van de zoekinput. Combineer met een eigen
 * label zoals vandaag.
 *
 * Verschillen met Dropdown-input:
 * - `clearable` i.p.v. "toon het kruisje als er een @clear-listener is"
 *   (getCurrentInstance-hack). Het kruisje wist nu zelf het model en de zoekterm en
 *   emit daarna nog `clear`.
 * - `item-label` i.p.v. `display-value` (botste met de leesmodus-term elders).
 */
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    TransitionRoot,
} from '@headlessui/vue'
import { autoUpdate, flip, offset, size, useFloating } from '@floating-ui/vue'
import { IconSelector, IconX } from '@tabler/icons-vue'
import VSpinner from '@/Components/BaseComponents/VSpinner.vue'
import { computed, ref } from 'vue'
import { useModalStack } from '@/useModalStack'

type Item = Record<string, any>

const model = defineModel<string | number | Item | null>()
const searchValue = defineModel<string>('searchValue')

const props = withDefaults(
    defineProps<{
        items?: Item[]
        /** Property die getoond wordt. */
        itemLabel: string
        /** Property die de waarde vormt (tenzij `returnObject`). */
        itemKey: string
        /** Vertaalsleutel. */
        placeholder?: string
        loading?: boolean
        disabled?: boolean
        /** Model krijgt het volledige item i.p.v. `item[itemKey]`. */
        returnObject?: boolean
        clearable?: boolean
        /**
         * Id van het zoekveld, zodat een `<label for>` erbij hoort. Zonder id kreeg
         * het veld een automatisch Headless-id en wees het label van VSelect naar een
         * onbestaand element: geen naam voor een schermlezer, klik op het label deed
         * niets (review #6967 R199, GH #6972).
         */
        inputId?: string | null
        /**
         * Maximaal aantal getoonde items; de rest via verder typen (zie de melding
         * onderaan de lijst). null = alles. Zie VSelect (review #6967 R026).
         */
        maxItems?: number | null
        /**
         * Property met het groepslabel. Gezet: een niet-kiesbare kop boven elke groep
         * (zoals een `<optgroup>`); de items moeten per groep na elkaar staan. Zo kan
         * VSelect ook gegroepeerde lijsten doorzoekbaar maken (btw-landcodes, GH #7020).
         */
        itemGroup?: string | null
        /**
         * Property die in het veld staat zodra een item gekozen is; standaard
         * `itemLabel`. Bv. enkel "BE" in het smalle landcodeveld, terwijl de lijst
         * "BE – België" toont (GH #7020).
         */
        itemDisplay?: string | null
        /** Minimale breedte van de lijst in px; standaard even breed als het veld. */
        listMinWidth?: number | null
    }>(),
    {
        items: () => [],
        placeholder: 'global.type_and_search',
        loading: false,
        disabled: false,
        returnObject: false,
        clearable: false,
        inputId: null,
        maxItems: null,
        itemGroup: null,
        itemDisplay: null,
        listMinWidth: null,
    }
)

const emit = defineEmits<{ clear: [] }>()

const { getStackCount } = useModalStack()

const comboboxButtonRef = ref<InstanceType<typeof ComboboxButton> | null>(null)

// Na een keuze krijgt de input opnieuw focus; dan mag de lijst niet meteen weer openen.
const justSelected = ref(false)

/**
 * Tab mag niet stil de eerste optie kiezen.
 *
 * De lijst opent bij focus en Headless UI maakt dan de eerste optie actief; zijn
 * Tab-handler kiest de actieve optie. Wie door een formulier tabde, koos zo ongemerkt
 * de eerste klant of het eerste project (review #6967 R197, GH #6972). Headless UI 1.7
 * biedt daar geen optie voor (`immediate` is in de Vue-versie niet aangesloten), dus
 * vangen we Tab op in de capture-fase, vóór zijn handler, en negeert de setter de keuze
 * die daarop volgt. Wie typte of met de pijltjes een optie aanwees, kiest met Tab wel
 * zoals gewoonlijk.
 */
let suppressTabSelection = false
let keyboardNavigated = false

const NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown']

function handleKeydownCapture(event: KeyboardEvent, isOpen: boolean): void {
    if (NAVIGATION_KEYS.includes(event.key)) {
        keyboardNavigated = true
        return
    }

    if (event.key !== 'Tab' || !isOpen) {
        return
    }

    if (!keyboardNavigated && !searchValue.value) {
        suppressTabSelection = true
        // Kiest Headless niets (geen actieve optie), dan de vlag na deze toets wissen.
        setTimeout(() => {
            suppressTabSelection = false
        })
    }
    keyboardNavigated = false
}

/**
 * Een klik die de gesloten lijst heropent (in het veld of op het pijltje) vuurt geen
 * focus-event, dus handleFocus wist de navigatievlag dan niet. Pijltje omlaag, lijst
 * dicht met het pijltje, heropenen met een klik en Tab koos zo toch weer stil de eerste
 * optie (review #6967 R197-vervolg, GH #6972).
 *
 * Escape hoeft hier niet apart: bij een leeg veld roept Headless dan change(null) aan
 * en wist de setter de vlag al; met een keuze maakt heropenen die keuze actief, zodat
 * Tab niets wijzigt.
 */
function handleMouseDownCapture(isOpen: boolean): void {
    if (!isOpen) {
        keyboardNavigated = false
    }
}

const selectedItem = computed<Item | null>({
    get() {
        if (model.value === null || model.value === undefined) {
            return null
        }

        if (props.returnObject) {
            return model.value as Item
        }

        // Vergelijken op stringvorm, zoals een native <select> (Vue's looseEqual): een
        // leeg model ('') mag niet als sleutel 0 tellen, wat `==` wel deed
        // (review #6967 R025, GH #6972).
        const key = String(model.value)
        return props.items.find((item) => String(item[props.itemKey]) === key) ?? null
    },
    set(newValue) {
        // Tab door een lijst waarin niets gekozen of getypt werd (zie handleKeydownCapture).
        if (suppressTabSelection) {
            suppressTabSelection = false
            return
        }
        keyboardNavigated = false
        if (newValue) {
            model.value = props.returnObject ? newValue : newValue[props.itemKey]
        } else {
            model.value = null
        }
        justSelected.value = true
    },
})

/**
 * Getoonde items; met `maxItems` enkel de eerste, de rest via verder typen.
 *
 * Het gekozen item staat er altijd bij. Anders kent Headless UI het niet, maakt het bij
 * openen de eerste optie actief en kiest het die bij een klik ernaast of Tab: de keuze
 * zou dan stil wijzigen.
 */
const visibleItems = computed<Item[]>(() => {
    if (props.maxItems === null || props.items.length <= props.maxItems) {
        return props.items
    }

    const visible = props.items.slice(0, props.maxItems)
    const selected = selectedItem.value
    if (selected && !visible.includes(selected) && props.items.includes(selected)) {
        visible.push(selected)
    }

    return visible
})

const hiddenItemCount = computed<number>(() => props.items.length - visibleItems.value.length)

/**
 * Sleutel voor de v-for. Met groepen hoort de groep erbij: dezelfde waarde mag in twee
 * groepen staan (de btw-landen hadden o.a. AT en DE in West-Europa én Europa) en dubbele
 * keys laten Vue items verwisselen.
 */
function itemRenderKey(item: Item): string {
    const key = String(item[props.itemKey])
    return props.itemGroup ? `${item[props.itemGroup] ?? ''}::${key}` : key
}

/** Kop tonen boven het eerste item van elke groep; lege groepen vallen zo vanzelf weg. */
function startsGroup(index: number): boolean {
    if (!props.itemGroup) {
        return false
    }
    const group = visibleItems.value[index]?.[props.itemGroup]
    if (!group) {
        return false
    }
    return index === 0 || visibleItems.value[index - 1]?.[props.itemGroup] !== group
}

function getDisplayValue(item: unknown): string {
    if (!item) {
        return ''
    }
    return (item as Item)[props.itemDisplay ?? props.itemLabel] ?? (item as Item)[props.itemLabel]
}

function handleInputChange(event: Event): void {
    const inputText = (event.target as HTMLInputElement).value
    searchValue.value = inputText

    if (inputText === '') {
        model.value = null
    }
}

/**
 * Focus opent de lijst, behalve direct na een keuze of bij autofocus van een
 * formulier (dan pas bij typen of klikken; zie useAutofocusFirstField).
 */
function handleFocus(isOpen: boolean, event?: FocusEvent): void {
    keyboardNavigated = false

    if (isSilentFocus(event)) {
        return
    }

    if (justSelected.value) {
        justSelected.value = false
        return
    }

    if (!isOpen) {
        comboboxButtonRef.value?.$el.click()
    }
}

/**
 * Klik in een input die al focus had: open de lijst.
 *
 * Na de stille autofocus van een modal vuurt een klik in het veld geen nieuw
 * focus-event, waardoor de lijst dicht bleef tot de gebruiker typte (GH #722 review).
 * We onthouden bij mousedown of het veld al focus had: zo niet, dan opent
 * handleFocus de lijst al en zou deze klik hem via de knop weer dichtklappen.
 */
let hadFocusOnPointerDown = false

function handleMouseDown(event: MouseEvent): void {
    hadFocusOnPointerDown = document.activeElement === event.currentTarget
}

function handleClick(isOpen: boolean): void {
    if (hadFocusOnPointerDown && !isOpen && !props.disabled) {
        comboboxButtonRef.value?.$el.click()
    }
    hadFocusOnPointerDown = false
}

function handleBlur(): void {
    if (!selectedItem.value) {
        searchValue.value = ''
    }
}

function clear(): void {
    model.value = null
    searchValue.value = ''
    emit('clear')
}

/**
 * De lijst hangt via Teleport in `body` en moet boven alles staan waarin een combobox kan
 * zitten: modals (vanaf 1000, +10 per niveau) én de panelen van VPopover/VFilter en
 * VFloatingMenu (1900). Vroeger was dat 50 zonder open modal, waardoor de zoeklijst in
 * een filterpaneel (VSelect met >10 opties) onder het paneel opende. Onder de toasts (2000).
 */
const computedZIndex = computed<number>(() => {
    const modalZIndex = 1000 + Math.max(getStackCount.value - 1, 0) * 10 + 5

    return Math.max(1950, modalZIndex)
})

const referenceEl = ref(null)
const floatingEl = ref(null)

const { floatingStyles } = useFloating(referenceEl, floatingEl, {
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [
        offset(4),
        flip(),
        size({
            // Lijst even breed als de input, of breder met `listMinWidth`.
            apply({ rects, elements }) {
                const width = Math.max(rects.reference.width, props.listMinWidth ?? 0)
                Object.assign(elements.floating.style, {
                    width: `${width}px`,
                })
            },
        }),
    ],
})

function focus(): void {
    ;(referenceEl.value as HTMLElement | null)?.querySelector('input')?.focus()
}

defineExpose({ focus })

const combinedStyles = computed(() => ({
    ...floatingStyles.value,
    zIndex: computedZIndex.value,
}))
</script>

<template>
    <Combobox
        v-model="selectedItem"
        nullable
        :disabled="disabled"
        v-slot="{ open }"
        as="div"
        class="v-combobox"
    >
        <div
            ref="referenceEl"
            class="relative w-full cursor-default overflow-hidden rounded border-gray-300 bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 border focus-visible:ring-offset-primary-10 sm:text-sm"
            :class="{
                'cursor-not-allowed bg-gray-100 text-gray-500 border-gray-200': disabled,
                'bg-white border-gray-300 cursor-default': !disabled,
            }"
            @keydown.capture="handleKeydownCapture($event, open)"
            @mousedown.capture="handleMouseDownCapture(open)"
        >
            <ComboboxInput
                :id="inputId ?? undefined"
                type="search"
                autocomplete="off"
                class="w-full border-none py-2 pl-3 text-sm leading-5 text-gray-900 focus:ring-0"
                :class="{
                    'bg-gray-100': disabled,
                    // Ruimte rechts voor het pijltje, en het kruisje/de spinner als die er
                    // kunnen staan. Altijd pr-14 liet in een smal veld (de btw-landcode
                    // in een prefix) geen plaats voor de tekst (GH #7020).
                    'pr-14': clearable || loading,
                    'pr-8': !clearable && !loading,
                }"
                :disabled="disabled"
                :display-value="getDisplayValue"
                :placeholder="$t(placeholder)"
                @change="handleInputChange"
                @focus="handleFocus(open, $event)"
                @mousedown="handleMouseDown"
                @click="handleClick(open)"
                @blur="handleBlur"
            />

            <button
                v-if="(model || searchValue) && !loading && clearable"
                type="button"
                @click="clear"
                :aria-label="$t('global.clear_selection')"
                :title="$t('global.clear_selection')"
                class="absolute inset-y-0 right-8 flex items-center text-gray-400 hover:text-gray-600"
            >
                <IconX class="w-5 h-5" aria-hidden="true" />
            </button>

            <div v-if="loading" class="absolute inset-y-0 right-8 flex items-center text-gray-400">
                <VSpinner size="sm" />
            </div>

            <ComboboxButton
                ref="comboboxButtonRef"
                class="absolute inset-y-0 right-0 flex items-center pr-2"
            >
                <IconSelector aria-hidden="true" class="h-5 w-5 text-gray-400"></IconSelector>
            </ComboboxButton>
        </div>

        <Teleport to="body">
            <TransitionRoot
                :show="open"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <ComboboxOptions
                    static
                    ref="floatingEl"
                    :style="combinedStyles"
                    class="max-h-60 w-full overflow-auto rounded bg-white py-1 text-base ring-1 ring-gray-300 focus:outline-none sm:text-sm"
                >
                    <div
                        v-if="loading"
                        class="relative cursor-default select-none px-4 py-2 text-gray-700"
                    >
                        {{ $t('global.loading') }}
                    </div>
                    <template v-else>
                        <VEmptyState
                            v-if="items.length === 0"
                            variant="inline"
                            title="global.no_results_found"
                        />

                        <template v-for="(item, index) in visibleItems" :key="itemRenderKey(item)">
                            <!-- Groepskop: geen ComboboxOption, dus niet kiesbaar en de
                                 pijltjestoetsen slaan hem over. -->
                            <li
                                v-if="startsGroup(index)"
                                role="presentation"
                                class="cursor-default select-none px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                                {{ item[itemGroup as string] }}
                            </li>
                            <ComboboxOption
                                as="template"
                                :value="item"
                                v-slot="{ selected, active }"
                            >
                                <li
                                    class="relative cursor-default select-none py-2 pr-4"
                                    :class="{
                                        'bg-primary-10 text-white': active,
                                        'text-gray-900': !active,
                                        'pl-2': !itemGroup,
                                        'pl-4': itemGroup,
                                    }"
                                >
                                    <span
                                        class="block truncate"
                                        :class="{ 'font-bold': selected, 'font-normal': !selected }"
                                    >
                                        {{ item[itemLabel] }}
                                    </span>
                                </li>
                            </ComboboxOption>
                        </template>

                        <li
                            v-if="hiddenItemCount > 0"
                            class="cursor-default select-none px-2 py-2 text-xs italic text-gray-500"
                        >
                            {{ $t('global.type_to_refine', { count: String(hiddenItemCount) }) }}
                        </li>
                    </template>
                </ComboboxOptions>
            </TransitionRoot>
        </Teleport>
    </Combobox>
</template>
